import React, { useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { TYPE_ORDER, getCardPrimaryType } from './deckTypeUtils';
import './DeckInsights.css';

const MANA_BAR_MAX_HEIGHT = 80;
const MANA_BAR_MIN_VISIBLE = 4;
const CMC_BUCKET_COUNT = 8;
const CMC_MAX_INDEX = 7;
const CMC_LABELS = ['0', '1', '2', '3', '4', '5', '6', '7+'];

const COLOR_DISPLAY_ORDER = ['W', 'U', 'B', 'R', 'G', 'M', 'C', 'Land'];
const COLOR_LABELS = { W: 'White', U: 'Blue', B: 'Black', R: 'Red', G: 'Green', M: 'Multicolor', C: 'Colorless', Land: 'Land' };
const COLOR_HEX = { W: '#f0e68c', U: '#4a90d9', B: '#aaa', R: '#e74c3c', G: '#2ecc71', M: '#f39c12', C: '#888', Land: '#8b7355' };

const ManaCurve = ({ sortedData }) => {
  const { cmcData, avgCmc } = useMemo(() => {
    const buckets = new Array(CMC_BUCKET_COUNT).fill(0);
    let totalCmc = 0;
    let totalNonLand = 0;
    sortedData.forEach((entry) => {
      const type = getCardPrimaryType(entry.type_line);
      if (type === 'Land') return;
      const cmc = Math.floor(entry.cmc || 0);
      const qty = entry.quantity || 1;
      const idx = Math.min(cmc, CMC_MAX_INDEX);
      buckets[idx] += qty;
      totalCmc += (entry.cmc || 0) * qty;
      totalNonLand += qty;
    });
    const avg = totalNonLand > 0 ? (totalCmc / totalNonLand).toFixed(1) : null;
    return { cmcData: buckets, avgCmc: avg };
  }, [sortedData]);

  const maxCount = Math.max(...cmcData, 1);

  return (
    <div className="dvv-mana-curve">
      <h5>Mana Curve</h5>
      <div className={`dvv-mana-bars ${isMobile ? 'dvv-mana-bars--mobile' : 'dvv-mana-bars--desktop'}`}>
        {cmcData.map((count, idx) => {
          const barHeight = maxCount > 0 ? Math.max((count / maxCount) * MANA_BAR_MAX_HEIGHT, count > 0 ? MANA_BAR_MIN_VISIBLE : 0) : 0;
          return (
            <div key={idx} className="dvv-mana-bucket">
              <span className="dvv-mana-count">{count > 0 ? count : ''}</span>
              <div className="dvv-mana-bar" style={{ height: barHeight }} />
              <span className="dvv-mana-label">{CMC_LABELS[idx]}</span>
            </div>
          );
        })}
      </div>
      {avgCmc !== null && (
        <div className="dvv-avg-cmc">Avg. CMC: {avgCmc}</div>
      )}
    </div>
  );
};

const ColorDistribution = ({ sortedData }) => {
  const entries = useMemo(() => {
    const counts = {};
    sortedData.forEach((entry) => {
      const qty = entry.quantity || 1;
      const isLand = getCardPrimaryType(entry.type_line) === 'Land';
      if (isLand) {
        counts['Land'] = (counts['Land'] || 0) + qty;
        return;
      }
      const raw = entry.colors_raw || [];
      let bucket;
      if (raw.length === 0) bucket = 'C';
      else if (raw.length === 1) bucket = raw[0];
      else bucket = 'M';
      counts[bucket] = (counts[bucket] || 0) + qty;
    });
    return COLOR_DISPLAY_ORDER
      .filter((c) => (counts[c] || 0) > 0)
      .map((c) => ({ color: c, count: counts[c] }));
  }, [sortedData]);

  if (entries.length === 0) return null;

  return (
    <div className="dvv-stats-block">
      <h5>Color Distribution</h5>
      <div className="dvv-color-rows">
        {entries.map(({ color, count }) => (
          <div key={color} className="dvv-color-row">
            <span className="dvv-color-pip" style={{ backgroundColor: COLOR_HEX[color] }} />
            <span className="dvv-color-label">{COLOR_LABELS[color]}</span>
            <span className="dvv-color-count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CardTypeBreakdown = ({ sortedData }) => {
  const rows = useMemo(() => {
    const counts = {};
    sortedData.forEach((entry) => {
      const type = getCardPrimaryType(entry.type_line);
      const qty = entry.quantity || 1;
      counts[type] = (counts[type] || 0) + qty;
    });
    return TYPE_ORDER.filter((t) => (counts[t] || 0) > 0).map((t) => ({ type: t, count: counts[t] }));
  }, [sortedData]);

  if (rows.length === 0) return null;

  return (
    <div className="dvv-stats-block">
      <h5>Card Types</h5>
      <div className="dvv-type-rows">
        {rows.map(({ type, count }) => (
          <div key={type} className="dvv-type-row">
            <span className="dvv-type-row-label">{type}</span>
            <span className="dvv-type-row-count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DeckInsights = ({ tableData }) => {
  if (!tableData || tableData.length === 0) return null;

  return (
    <div className="dvv-insights-wrapper">
      <ManaCurve sortedData={tableData} />
      <div className={`dvv-stats-row ${isMobile ? 'dvv-stats-row--mobile' : 'dvv-stats-row--desktop'}`}>
        <ColorDistribution sortedData={tableData} />
        <CardTypeBreakdown sortedData={tableData} />
      </div>
    </div>
  );
};

export default DeckInsights;
